import { deckService } from './DeckService';
import { profileStore } from './ProfileStore';
import type { FlashCard } from '../types/deck';

export interface SessionResult {
  cardId: string;
  correct: boolean;
  timestamp: number;
}

export interface RunData {
  active: boolean;
  currentSubject: string | null;
  rootIdQueue: string[];
  currentCardIndex: number;
  reviewQueue: string[];
  streak: number;
  xpEarned: number;
  coinsEarned: number;
  sessionResults: SessionResult[];
}

const XP_PER_CORRECT = 10;
const COINS_PER_CORRECT = 5;
const STREAK_MULTIPLIER_THRESHOLD = 5;
const STREAK_MULTIPLIER = 2;

export class RunState {
  private runData: RunData;

  constructor() {
    this.runData = this.createEmptyRun();
  }

  private createEmptyRun(): RunData {
    return {
      active: false,
      currentSubject: null,
      rootIdQueue: [],
      currentCardIndex: 0,
      reviewQueue: [],
      streak: 0,
      xpEarned: 0,
      coinsEarned: 0,
      sessionResults: [],
    };
  }

  /**
   * Start a new run filtered by subject
   */
  startRun(subject: string): void {
    const filteredCards = deckService.getCardsBySubject(subject);

    // Shuffle the cards for variety
    const shuffled = [...filteredCards];
    this.shuffleArray(shuffled);

    this.runData = {
      active: true,
      currentSubject: subject,
      rootIdQueue: shuffled.map(card => card.id),
      currentCardIndex: 0,
      reviewQueue: [],
      streak: 0,
      xpEarned: 0,
      coinsEarned: 0,
      sessionResults: [],
    };

    console.info(`Started run for ${subject} with ${shuffled.length} cards`);
  }

  /**
   * Record an answer and update progression
   */
  recordAnswer(correct: boolean, cardId: string): { xp: number; coins: number; streak: number } {
    const result: SessionResult = {
      cardId,
      correct,
      timestamp: Date.now(),
    };
    this.runData.sessionResults.push(result);

    let xpGained = 0;
    let coinsGained = 0;

    if (correct) {
      this.runData.streak++;
      // Remove from review queue if it was waiting for retry
      this.runData.reviewQueue = this.runData.reviewQueue.filter(id => id !== cardId);

      // Calculate XP with streak multiplier
      const multiplier = this.runData.streak >= STREAK_MULTIPLIER_THRESHOLD ? STREAK_MULTIPLIER : 1;
      xpGained = XP_PER_CORRECT * multiplier;
      coinsGained = COINS_PER_CORRECT;

      this.runData.xpEarned += xpGained;
      this.runData.coinsEarned += coinsGained;

      // Update profile
      profileStore.addXP(xpGained);
      profileStore.addCoins(coinsGained);
      profileStore.recordCorrectAnswer(cardId);

      if (this.runData.currentSubject) {
        profileStore.updateSubjectStreak(this.runData.currentSubject, this.runData.streak);
      }
    } else {
      // Reset streak on wrong answer
      this.runData.streak = 0;
      profileStore.recordWrongAnswer(cardId);
      // Queue card for immediate review if not already queued
      if (!this.runData.reviewQueue.includes(cardId)) {
        this.runData.reviewQueue.push(cardId);
      }
    }

    return {
      xp: xpGained,
      coins: coinsGained,
      streak: this.runData.streak,
    };
  }

  /**
   * Get the next card in the queue
   */
  getNextCard(): FlashCard | null {
    if (!this.runData.active) return null;

    let nextId: string | undefined;

    if (this.runData.reviewQueue.length > 0) {
      nextId = this.runData.reviewQueue.shift();
    } else if (this.runData.currentCardIndex < this.runData.rootIdQueue.length) {
      nextId = this.runData.rootIdQueue[this.runData.currentCardIndex];
      this.runData.currentCardIndex++;
    }

    if (!nextId) return null;

    const card = deckService.getCardById(nextId);
    if (!card) {
      // Skip missing cards and try next
      return this.getNextCard();
    }
    return card;
  }

  /**
   * Get the current card without advancing
   */
  getCurrentCard(): FlashCard | null {
    if (!this.runData.active) return null;

    const index = Math.max(0, this.runData.currentCardIndex - 1);
    if (index >= this.runData.rootIdQueue.length) return null;

    const cardId = this.runData.rootIdQueue[index];
    return deckService.getCardById(cardId) || null;
  }

  /**
   * Check if the run has more cards
   */
  hasMoreCards(): boolean {
    return (
      this.runData.currentCardIndex < this.runData.rootIdQueue.length ||
      this.runData.reviewQueue.length > 0
    );
  }

  /**
   * End the current run
   */
  endRun(): RunData {
    const finalData = { ...this.runData };
    this.runData = this.createEmptyRun();
    return finalData;
  }

  /**
   * Get current run data
   */
  getRunData(): RunData {
    return { ...this.runData };
  }

  /**
   * Check if a run is active
   */
  isRunActive(): boolean {
    return this.runData.active;
  }

  /**
   * Get current streak
   */
  getStreak(): number {
    return this.runData.streak;
  }

  /**
   * Get XP earned this run
   */
  getXPEarned(): number {
    return this.runData.xpEarned;
  }

  /**
   * Get coins earned this run
   */
  getCoinsEarned(): number {
    return this.runData.coinsEarned;
  }

  /**
   * Get current subject
   */
  getCurrentSubject(): string | null {
    return this.runData.currentSubject;
  }

  /**
   * Get progress through the run (0-100)
   */
  getProgress(): number {
    if (this.runData.rootIdQueue.length === 0) return 0;
    return Math.round((this.runData.currentCardIndex / this.runData.rootIdQueue.length) * 100);
  }

  /**
   * Get remaining cards count
   */
  getRemainingCards(): number {
    return Math.max(0, this.runData.rootIdQueue.length - this.runData.currentCardIndex) +
      this.runData.reviewQueue.length;
  }

  private shuffleArray<T>(array: T[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }
}

// Singleton instance
export const runState = new RunState();
